import { useCallback, useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import { AxiosError } from "axios";
import { initializeApp } from "firebase/app";
import {
    GoogleAuthProvider,
    OAuthProvider,
    getAuth,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    deleteUser,
    User as FirebaseUser,
    UserCredential
} from "firebase/auth";

import { StatisticsContainer } from "../statistics/statistics.container";
import { useFirebaseToken } from "./use-firebase-token.util";
import { firebaseConfig } from "../../utilities/constants/constants";
import { useTimer } from "../../utilities/timer/use-timer.util";
import { isAuthDomain } from "../../utilities/routes/routes.util";
import { isAxios } from "../../utilities/errors/errors.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../utilities/localstorage-dealer/localstorage-setter.util";
import { AuthEvents } from "../../enums/auth-events.enum";
import { CompanyType } from "../../enums/company-type.enum";
import { FirebaseErrorCode } from "../../enums/firebase-error-code.enum";
import { UserRole } from "../../enums/user-role.enum";
import { User } from "../../interfaces/comps/user.interface";
import { KnowledgeBase } from "../../interfaces/admin/knowledge-base.interface";
import { Config } from "../../interfaces/comps/config.interface";
import { KBConfig } from "../../interfaces/admin/knowledge-base-config.interface";
import { ACCESS_TOKEN_KEY } from "../../../api/base";
import {
    fetchVersion,
    fetchConfig,
    fetchTenant
} from "../../../api/admin/config/endpoints-referrers";
import {
    registerFirebase,
    sendVerificationEmail as sendVerificationEmailAPI,
    sendPasswordReset as sendPasswordResetAPI
} from "../../../api/auth/endpoints-referrers";
import {
    fetchUser,
    updateUser,
    updateUserAgent,
    updateUserKB
} from "../../../api/users/endpoints-referrers";
import {
    fetchKnowledgeBase,
    fetchKnowledgeBases,
    fetchKnowledgeBaseConfig
} from "../../../api/admin/knowledge-base/endpoints-referrers";

function useFirebaseAuth() {
    const googleProvider = new GoogleAuthProvider();
    const microsoftProvider = new OAuthProvider("microsoft.com");
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    const { initialize: initializeStatistics, reset: resetStatistics } =
        StatisticsContainer.useContainer();
    const [tenantInitialized, setTenantInitialized] = useState<boolean>(false);
    const [config, setConfig] = useState<Config>();
    const [version, setVersion] = useState<string>();
    const [isTrial, setIsTrial] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>();
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>();
    const [user, setUser] = useState<User | null>(null);
    const [email, setEmail] = useState<string>();
    const [emailVerified, setEmailVerified] = useState<boolean>();
    const [providerLoading, setProviderLoading] = useState<boolean>(false);
    const [sendVerificationWaitingTime, setSendVerificationWaitingTime] = useState<number>();
    const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase>();
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [knowledgeBaseConfig, setKnowledgeBaseConfig] = useState<KBConfig>();
    const {
        currentAccessToken,
        isAuthenticated,
        isAuthenticatedState,
        setAccessToken,
        setTokens,
        clearToken,
        updateCurrentAccessToken
    } = useFirebaseToken(auth, useCallback(clearUser, []));
    const { timerStarted, timerTimeLeft, startTimer, stopTimer } = useTimer();

    // Initialize Firebase tenant ID
    useEffect(() => {
        // Pass the tenant origin explicitly (set by the auth-domain pages) so resolution does
        // not depend solely on the sessionStorage -> header round-trip, which can race.
        const storedTenantOrigin = sessionStorage.getItem("auth_tenant_origin") || undefined;
        fetchTenant(storedTenantOrigin)
            .then(response => {
                if (response.data?.tenantId) {
                    auth.tenantId = response.data.tenantId;
                    console.log("Firebase tenant initialized:", response.data.tenantId);
                }
            })
            .catch(error => {
                console.error("Failed to fetch tenant ID:", error);
            })
            .finally(() => {
                setTenantInitialized(true);
            });
    }, [auth]);

    useEffect(() => {
        setIsAdmin(
            !user
                ? undefined
                : user.role === UserRole.superAdmin ||
                      user.role === UserRole.admin ||
                      user.role === UserRole.manager
        );
        setIsSuperAdmin(!user ? undefined : user.role === UserRole.superAdmin);
    }, [user]);

    useEffect(() => {
        if (currentAccessToken && user) {
            initializeStatistics();
        } else {
            resetStatistics();
        }
    }, [currentAccessToken, user, initializeStatistics, resetStatistics]);

    useEffect(() => {
        fetchVersion().then(response => {
            setVersion(response.data);
        });
    }, []);

    useEffect(() => {
        if (user) {
            refreshConfig();
        } else {
            setConfig(undefined);
        }
    }, [user]);

    const refreshConfig = async () => {
        const response = await fetchConfig();
        setConfig(response.data);
    };

    useEffect(() => {
        if (user?.currentKnowledgeBaseId) {
            loadKnowledgeBaseConfig(user.companyId, user.currentKnowledgeBaseId);
        } else {
            setKnowledgeBaseConfig(undefined);
        }
    }, [user]);

    const loadKnowledgeBaseConfig = async (companyId: number, kbUid: string) => {
        const response = await fetchKnowledgeBaseConfig(companyId, kbUid);
        setKnowledgeBaseConfig(response.data || undefined);
    };

    const loadUser = useCallback(async () => {
        console.log("[loadUser] Fetching user data from backend...");
        const {
            data: { ...user }
        } = await fetchUser();

        console.log(
            "[loadUser] User data fetched:",
            user.email,
            "companyId:",
            user.companyId,
            "emailVerified:",
            user.emailVerified
        );
        setUser(user);
        // Set emailVerified from backend user data (source of truth from Firebase claims)
        setEmailVerified(user.emailVerified);
        setEmail(user.email);
        console.log("[loadUser] Loading knowledge bases...");
        await loadKnowledgeBases(user.companyId);
        console.log("[loadUser] Knowledge bases loaded");
    }, []);

    const loadKnowledgeBases = async (companyId: number) => {
        const {
            data: { items }
        } = await fetchKnowledgeBases(companyId);
        setKnowledgeBases(items);
    };

    const loadKnowledgeBase = async (companyId: number, kbUid: string) => {
        const {
            data: { ...kb }
        } = await fetchKnowledgeBase(companyId, kbUid);
        // TODO will be returned
        setKnowledgeBase(kb);
        // setKnowledgeBase({ ...kb, expiringTrialPeriod: 0 });
        setIsTrial(kb.companyType === CompanyType.trial || kb.companyType === CompanyType.expired);

        // TODO will be deleted
        // const expiredDate = new Date().getTime() + 14 * 24 * 60 * 60 * 1000;
        // const createdDate = new Date(kb.createdAt).getTime();
        // const expiringTrialPeriod = Math.trunc(
        //     (expiredDate - createdDate) / (24 * 60 * 60 * 1000)
        // );
        // const companyType = !themeContainer.isTrial
        //     ? CompanyType.basic
        //     : expiringTrialPeriod > 0
        //       ? CompanyType.trial
        //       : CompanyType.expired;
        // setKnowledgeBase({ ...kb, companyType, expiringTrialPeriod });
        // setIsTrial(companyType === CompanyType.trial || companyType === CompanyType.expired);
    };

    const updateUserKnowledgeBase = async (kbId: string) => {
        const {
            data: { ...user }
        } = await updateUserKB(kbId);
        setUser(user);
    };

    // P3-08 "All assistants": bind (or clear) the explicit agent selection.
    const updateUserAgentSelection = async (agentUid: string | null) => {
        const {
            data: { ...user }
        } = await updateUserAgent(agentUid);
        setUser(user);
    };

    const refreshKnowledgeBase = useCallback(async () => {
        if (user?.currentKnowledgeBaseId) {
            loadKnowledgeBase(user.companyId, user.currentKnowledgeBaseId);
        } else {
            setKnowledgeBase(undefined);
        }
    }, [user]);

    useEffect(() => {
        refreshKnowledgeBase();
    }, [refreshKnowledgeBase, user]);

    const logout = useCallback(() => {
        signOut(auth).finally(async () => {
            await clearToken(true);
            stopTimer();
            setDataToLocalStorage(AuthEvents.LOGOUT, new Date().toISOString());
            removeDataToLocalStorage(AuthEvents.VERIFICATION_SENDING_TIME);
        });
    }, [auth, clearToken, stopTimer]);

    const loadUserData = useCallback(() => {
        console.log("[loadUserData] Called, loading user from backend...");
        // Always load user data from backend when authenticated
        // The backend will return emailVerified from Firebase token claims
        loadUser().catch(error => {
            // Don't logout on 422 (user not found) - this is expected during OAuth registration
            // The callback handler will register the user and call loadUser again
            if (isAxios(error, 422)) {
                console.log("[loadUserData] User not found (422), skipping logout");
                return;
            }
            console.error("[loadUserData] loadUser failed, logging out");
            logout();
        });
    }, [loadUser, logout]);

    useEffect(() => {
        console.log(
            "[useEffect] Authentication state changed, isAuthenticated:",
            isAuthenticated()
        );
        if (isAuthenticated()) {
            // During an invite-driven registration visit, the registration flow owns
            // auth (it signs out any stale session and re-authenticates via the invite).
            // Auto-loading the user here would 401 against a deleted/not-yet-provisioned
            // user and bounce to /login, dropping the invite. Skip it.
            const params = new URLSearchParams(window.location.search);
            if (window.location.pathname.startsWith("/registration") && params.get("invite")) {
                console.log("[useEffect] Registration invite visit; skipping auto loadUserData");
                return;
            }
            console.log("[useEffect] Calling loadUserData...");
            loadUserData();
        }
    }, [isAuthenticated, loadUserData]);

    // TODO will be returned
    // useEffect(() => {
    //     setIsTrial(user?.companyType === CompanyType.trial);
    // }, [user]);

    useEffect(() => {
        const handleStorageEventListener = async (event: WindowEventMap["storage"]) => {
            if (event.key === AuthEvents.LOGOUT) {
                await clearToken();
                stopTimer();
            } else if (
                (event.key === AuthEvents.LOGIN ||
                    event.key === AuthEvents.VERIFICATION_SENDING_TIME ||
                    event.key === AuthEvents.EMAIL_CONFIRMED_TIME) &&
                isAuthenticated()
            ) {
                location.reload();
            } else if (event.key === ACCESS_TOKEN_KEY && isAuthenticated()) {
                updateCurrentAccessToken();
            }
        };

        window.addEventListener("storage", handleStorageEventListener);

        return () => {
            window.removeEventListener("storage", handleStorageEventListener);
        };
    }, [isAuthenticated, clearToken, stopTimer, updateCurrentAccessToken]);

    useEffect(() => {
        setSendVerificationWaitingTime(timerStarted ? timerTimeLeft : undefined);
    }, [timerStarted, timerTimeLeft]);

    // Helper to wait for tenant initialization
    const waitForTenantInit = async () => {
        if (tenantInitialized) return;

        // Wait up to 5 seconds for tenant initialization
        const maxWait = 5000;
        const startTime = Date.now();

        while (!tenantInitialized && Date.now() - startTime < maxWait) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (!tenantInitialized) {
            console.warn("Tenant initialization timeout, proceeding without tenant ID");
        }
    };

    const signIn = async (email: string, password: string, invite: string | null) => {
        try {
            console.log("[signIn] Starting email/password sign-in...");
            await waitForTenantInit();
            const response = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();
            console.log("[signIn] Got Firebase token, emailVerified:", firebaseUser.emailVerified);

            setAccessToken(token);
            setEmailVerified(firebaseUser.emailVerified);
            setEmail(firebaseUser.email ?? undefined);

            if (!firebaseUser.emailVerified) {
                console.log("[signIn] Email not verified, sending verification...");
                try {
                    await sendVerification();
                } catch (verificationError: any) {
                    // Check if it's a rate limit error (TOO_MANY_ATTEMPTS_TRY_LATER)
                    const errorMsg = verificationError?.response?.data?.message || "";
                    if (errorMsg.includes("TOO_MANY_ATTEMPTS_TRY_LATER")) {
                        // Rate limited - proceed to email-confirm page so user can wait
                        console.log("[signIn] Rate limited, proceeding to email-confirm");
                    } else {
                        // Other error - throw with specific code for login page
                        console.error("[signIn] Verification email failed:", verificationError);
                        throw {
                            code: FirebaseErrorCode.verificationEmailFailed,
                            originalError: verificationError
                        };
                    }
                }
                // On login domain, return early after sending verification
                if (isAuthDomain()) {
                    console.log("[signIn] On login domain, sign-in complete (unverified)");
                    return Promise.resolve(response);
                }
            } else {
                console.log("[signIn] Email verified");
                if (invite) {
                    console.log("[signIn] Registering Firebase user with invite...");
                    await registerFirebaseUser(firebaseUser, email, invite);
                }

                // On login domain, return early without loading user data
                // The auth domain page will handle token extraction and redirect
                if (isAuthDomain()) {
                    console.log("[signIn] On login domain, skipping loadUser");
                    setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });
                    console.log("[signIn] Sign-in complete (login domain)");
                    return Promise.resolve(response);
                }

                console.log("[signIn] Loading user data...");
                await loadUser();
                console.log("[signIn] User data loaded");
                setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());
            }

            console.log("[signIn] Setting tokens in localStorage...");
            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });
            console.log("[signIn] Sign-in complete");

            return Promise.resolve(response);
        } catch (error: any) {
            // if (error?.code === FirebaseErrorCode.manyRequests) {
            //     return Promise.resolve({});
            // } else {
            //     logout();
            //     return Promise.reject(error);
            // }
            logout();
            return Promise.reject(error);
        }
    };

    // Self-service company creation is retired (D7, 2026-09-25):
    // registration always requires a valid invite, so the old
    // companyName / forceCreateKB plumbing is gone from this chain.
    const signUp = async (
        email: string,
        password: string,
        invite: string | null,
        name?: string,
        position?: string
    ) => {
        try {
            console.log("[signUp] Starting email/password sign-up...");
            await waitForTenantInit();
            const response = await createUserWithEmailAndPassword(auth, email, password);
            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();
            console.log("[signUp] Got Firebase token");

            setAccessToken(token);
            setEmailVerified(firebaseUser.emailVerified);
            setEmail(firebaseUser.email ?? undefined);

            console.log("[signUp] Registering Firebase user...");
            await registerFirebaseUser(firebaseUser, email, invite, name, position);
            console.log("[signUp] Sending verification email...");
            await sendVerification();
            console.log("[signUp] Setting tokens...");
            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });
            console.log("[signUp] Sign-up complete");

            return Promise.resolve(response);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    // Block OAuth when a tenant is expected (auth/login domain carries a tenant origin) but the
    // Firebase tenant was not resolved. Without this, signInWithPopup runs against the default
    // user pool and mints a tenant-less user that the backend cannot register.
    const ensureTenantResolved = () => {
        if (sessionStorage.getItem("auth_tenant_origin") && !auth.tenantId) {
            throw new Error("Tenant could not be resolved. Please refresh the page and try again.");
        }
    };

    const signInWithGoogle = async (invite: string | null) => {
        try {
            // Check tenant is initialized synchronously to avoid popup blocker
            if (!tenantInitialized) {
                throw new Error("Tenant not initialized. Please wait and try again.");
            }
            ensureTenantResolved();
            const response = await signInWithPopup(auth, googleProvider);

            // On login domain, return the credential without API calls
            // The auth domain page will handle token extraction and redirect
            if (isAuthDomain()) {
                return Promise.resolve(response);
            }

            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    const signInWithMicrosoft = async (invite: string | null) => {
        try {
            // Check tenant is initialized synchronously to avoid popup blocker
            if (!tenantInitialized) {
                throw new Error("Tenant not initialized. Please wait and try again.");
            }
            ensureTenantResolved();
            const response = await signInWithPopup(auth, microsoftProvider);

            // On login domain, return the credential without API calls
            // The auth domain page will handle token extraction and redirect
            if (isAuthDomain()) {
                return Promise.resolve(response);
            }

            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    const signInWithProvider = async (response: UserCredential, invite: string | null) => {
        try {
            setProviderLoading(true);

            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();
            setAccessToken(token);

            if (!firebaseUser.email) {
                throw { code: FirebaseErrorCode.invalidEmail };
            }

            setEmail(firebaseUser.email);
            setEmailVerified(firebaseUser.emailVerified || !!firebaseUser.providerId);
            await getOrRegisterUser(firebaseUser, firebaseUser.email, invite);

            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });
            setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        } finally {
            setProviderLoading(false);
        }
    };

    const getOrRegisterUser = async (user: FirebaseUser, email: string, invite: string | null) => {
        // D7: no invite, no registration — the trial-host no-invite branch
        // is gone with self-service signup.
        if (invite) {
            await registerFirebaseUser(user, email, invite);
            await loadUser();
        } else {
            try {
                await loadUser();
            } catch (error) {
                if (error instanceof AxiosError && error.response?.status === 422) {
                    await deleteUser(user);
                    throw { code: FirebaseErrorCode.invalidInvite };
                }
                throw error;
            }
        }
    };

    const registerFirebaseUser = async (
        user: FirebaseUser,
        email: string,
        invite: string | null,
        name?: string,
        position?: string
    ) => {
        const signupData = {
            email,
            from: invite,
            firebaseId: user.uid,
            firstName: name,
            position
        };
        try {
            await registerFirebase(signupData);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 409) {
                await updateUser(user.uid);
            } else {
                await clearNonexistentUser(user);
                throw isAxios(error, 403, "Invalid invite")
                    ? { code: FirebaseErrorCode.invalidInvite }
                    : isAxios(error, 403, "Invalid email format")
                      ? { code: FirebaseErrorCode.invalidEmail }
                      : isAxios(error, 403, "Database change denied")
                        ? { code: FirebaseErrorCode.forbiddenInvite }
                        : isAxios(error, 403, "Invite link does not match your email")
                          ? { code: FirebaseErrorCode.inappropriateInvite }
                          : error;
            }
        }
    };

    const clearNonexistentUser = async (user: FirebaseUser) => {
        try {
            await fetchUser();
        } catch (userError) {
            if (userError instanceof AxiosError && userError.response?.status === 422) {
                await deleteUser(user);
            }
        }
    };

    const sendVerification = async () => {
        try {
            setDataToLocalStorage(AuthEvents.VERIFICATION_SENDING_TIME, new Date().toISOString());
            startTimer(60);

            // Always use backend endpoint which uses Firebase Admin SDK
            console.log("[sendVerification] using backend endpoint");
            const loginUrl = process.env.REACT_APP_LOGIN_DOMAIN;
            if (!loginUrl) {
                throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
            }
            await sendVerificationEmailAPI(loginUrl);

            return Promise.resolve(true);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const sendPasswordReset = async (
        email: string,
        _returnUrl?: string,
        _tenantOrigin?: string
    ) => {
        try {
            // Always use backend endpoint which uses Firebase Admin SDK
            console.log("[sendPasswordReset] using backend endpoint");
            const loginUrl = process.env.REACT_APP_LOGIN_DOMAIN;
            if (!loginUrl) {
                throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
            }
            await sendPasswordResetAPI(email, loginUrl);

            return Promise.resolve(true);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    function clearUser() {
        setUser(null);
        setEmail(undefined);
        setEmailVerified(undefined);
    }

    /**
     * Provisions an OAuth user in the backend after redirect from login domain
     * Should be called on tenant domain after tokens are set
     * @param email - User's email from OAuth
     * @param invite - Optional invite code
     * @param firebaseUid - Firebase user ID
     * @returns Promise that resolves when user is provisioned
     */
    const provisionOAuthUser = async (
        email: string,
        invite: string | null,
        firebaseUid: string
    ): Promise<void> => {
        setEmail(email);
        setEmailVerified(true); // OAuth users are always email verified

        // Call backend to register/provision the user. D7: no invite, no
        // registration — self-service company creation is retired.
        if (invite) {
            const signupData = {
                email,
                from: invite,
                firebaseId: firebaseUid,
                firstName: undefined,
                position: undefined
            };

            try {
                await registerFirebase(signupData);
            } catch (error) {
                if (error instanceof AxiosError && error.response?.status === 409) {
                    await updateUser(firebaseUid);
                } else {
                    throw error;
                }
            }
        }

        // Store auth event
        setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());
    };

    // Debug helper for testing auth flows
    // Enable with REACT_APP_AUTH_DEBUG=true in your .env file
    useEffect(() => {
        if (process.env.REACT_APP_AUTH_DEBUG === "true") {
            (window as any).__debugAuth = {
                // Test token REFRESH via REST API
                // Corrupts access token but keeps refresh token valid
                // Expected: 401 → REST API refresh → retry → success
                simulateRefreshableToken: () => {
                    console.log("[debugAuth] Corrupting access token...");
                    console.log("[debugAuth] Refresh token kept valid for REST API");
                    localStorage.setItem("accessToken", "invalid_expired_token");
                    console.log("[debugAuth] Done! Make an API call to trigger 401.");
                    console.log("[debugAuth] Expected: 401 → REST API refresh → success");
                },
                // Test NO refresh possible: invalid BOTH tokens
                // Expected: 401 → REST API fails → logout → redirect to login
                simulateExpiredToken: () => {
                    console.log("[debugAuth] Corrupting BOTH tokens...");
                    localStorage.setItem("accessToken", "invalid_expired_token");
                    localStorage.setItem("refreshToken", "invalid_refresh_token");
                    console.log("[debugAuth] Done! Make an API call to trigger 401.");
                    console.log("[debugAuth] Expected: 401 → refresh fails → logout");
                },
                // Full logout (clears everything)
                fullLogout: () => logout(),
                // Check current auth state
                getState: () => ({
                    hasFirebaseUser: !!auth.currentUser,
                    firebaseUid: auth.currentUser?.uid,
                    hasLocalStorageTokens: isAuthenticated(),
                    isAuthenticatedState,
                    user: user?.email
                }),
                // Force clear tokens (simulates token cleared by another tab)
                forceClearTokens: () => clearToken()
            };
            console.log("[debugAuth] Debug helpers available at window.__debugAuth");
        }
        return () => {
            if (process.env.REACT_APP_AUTH_DEBUG === "true") {
                delete (window as any).__debugAuth;
            }
        };
    }, [auth, isAuthenticated, isAuthenticatedState, user, logout, clearToken]);

    // Tenant admin feature gate (tenant_admin_permissions_design.md §6):
    // super admins bypass; company admins need the tenant's resolved
    // permission (delivered on the login payload). Managers and users never
    // gain a feature through it.
    const hasAdminFeature = useCallback(
        (feature: string): boolean => {
            if (isSuperAdmin) return true;
            if (user?.role !== UserRole.admin) return false;
            return !!user?.adminFeatures?.[feature];
        },
        [isSuperAdmin, user]
    );

    return {
        isAuthenticated,
        isAuthenticatedState,
        currentAccessToken,
        user,
        isSuperAdmin,
        isAdmin,
        hasAdminFeature,
        isTrial,
        email,
        emailVerified,
        sendVerificationWaitingTime,
        knowledgeBase,
        knowledgeBases,
        knowledgeBaseConfig,
        config,
        version,
        providerLoading,
        refreshConfig,
        loadUser,
        updateUserKnowledgeBase,
        updateUserAgentSelection,
        refreshKnowledgeBase,
        loadKnowledgeBaseConfig,
        loadKnowledgeBases,
        setUser,
        setTokens,
        setEmail,
        setEmailVerified,
        signIn,
        signUp,
        signInWithGoogle,
        signInWithMicrosoft,
        sendVerification,
        sendPasswordReset,
        logout,
        provisionOAuthUser
    };
}

export const FirebaseAuthContainer = createContainer(useFirebaseAuth);
